import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, BadgePercent, CalendarClock, Crown, FileText, HelpCircle, Loader2, Plus, Power, Trash2, TrendingUp, Users, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL, QUOTE_STATUS } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function SupplierHub() {
    const [analytics, setAnalytics] = useState(null);
    const [customers, setCustomers] = useState(null);
    const [quoteStats, setQuoteStats] = useState(null);
    const [campaigns, setCampaigns] = useState(null);
    const [offers, setOffers] = useState([]);
    const [invoices, setInvoices] = useState(null);
    const [questions, setQuestions] = useState(null);
    const [answers, setAnswers] = useState({});
    const [ansBusy, setAnsBusy] = useState(null);
    const [campOpen, setCampOpen] = useState(false);
    const [campForm, setCampForm] = useState({ offer_id: "", name: "", discount_percent: "10", days: "7" });
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/supplier/sales-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
        api.get("/supplier/customers").then((r) => setCustomers(r.data)).catch(() => {});
        api.get("/supplier/quote-stats").then((r) => setQuoteStats(r.data)).catch(() => {});
        api.get("/supplier/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
        api.get("/inventory").then((r) => setOffers(r.data.items || [])).catch(() => {});
        api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
        api.get("/questions").then((r) => setQuestions(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const createCampaign = async () => {
        if (!campForm.offer_id || !campForm.name.trim()) return toast.error("Ürün ve kampanya adı zorunludur");
        setBusy(true);
        try {
            await api.post("/supplier/campaigns", {
                offer_id: campForm.offer_id,
                name: campForm.name,
                discount_percent: Number(campForm.discount_percent),
                days: Number(campForm.days) || 7,
            });
            toast.success("Kampanya yayında — alıcılar indirimli fiyatı anında görür");
            setCampOpen(false);
            setCampForm({ offer_id: "", name: "", discount_percent: "10", days: "7" });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Oluşturulamadı");
        } finally {
            setBusy(false);
        }
    };

    const answerQuestion = async (q) => {
        const text = (answers[q.question_id] || "").trim();
        if (!text) return toast.error("Yanıt yazın");
        setAnsBusy(q.question_id);
        try {
            const r = await api.post(`/questions/${q.question_id}/answer`, { answer: text });
            toast.success(r.data.late ? "Yanıt gönderildi (süre aşımı olarak işaretlendi)" : "Yanıt gönderildi ve mağazaya bildirildi");
            setAnswers((a) => ({ ...a, [q.question_id]: "" }));
            api.get("/questions").then((r2) => setQuestions(r2.data)).catch(() => {});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Yanıt gönderilemedi");
        } finally {
            setAnsBusy(null);
        }
    };

    const toggleCamp = async (c) => {        await api.post(`/supplier/campaigns/${c.campaign_id}/toggle`);
        toast.success(c.active ? "Kampanya duraklatıldı" : "Kampanya tekrar aktif");
        load();
    };

    const deleteCamp = async (c) => {
        await api.delete(`/supplier/campaigns/${c.campaign_id}`);
        toast.success("Kampanya silindi");
        load();
    };

    const kpiCards = analytics ? [
        { label: "Toplam Ciro", value: fmtTL(analytics.kpis.revenue), tid: "hub-kpi-revenue" },
        { label: "Sipariş", value: analytics.kpis.orders, tid: "hub-kpi-orders" },
        { label: "Ort. Sipariş Tutarı", value: fmtTL(analytics.kpis.avg_order), tid: "hub-kpi-avg" },
        { label: "Satılan Ürün (birim)", value: analytics.kpis.items_sold.toLocaleString("tr-TR"), tid: "hub-kpi-items" },
    ] : [];

    const openInv = (invoices || []).filter((i) => i.status === "issued");
    const tahsilat = invoices ? {
        overdueCount: openInv.filter((i) => i.payment_overdue).length,
        overdueTotal: openInv.filter((i) => i.payment_overdue).reduce((a, i) => a + i.total, 0),
        upcomingCount: openInv.filter((i) => !i.payment_overdue && i.payment_days_left != null && i.payment_days_left <= 7).length,
        upcomingTotal: openInv.filter((i) => !i.payment_overdue && i.payment_days_left != null && i.payment_days_left <= 7).reduce((a, i) => a + i.total, 0),
        confirmCount: openInv.filter((i) => i.buyer_paid).length,
        openTotal: openInv.reduce((a, i) => a + i.total, 0),
    } : null;

    return (
        <div className="space-y-6" data-testid="supplier-hub-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Satış Paneli</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Satış analitiği, müşterileriniz, kampanyalarınız ve teklif performansınız</p>
                </div>
                <Button onClick={() => setCampOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="campaign-new-button">
                    <BadgePercent className="mr-2 h-4 w-4" /> Kampanya Oluştur
                </Button>
            </div>

            <Tabs defaultValue="analitik" data-testid="hub-tabs">
                <TabsList>
                    <TabsTrigger value="analitik" data-testid="hub-tab-analitik"><TrendingUp className="mr-2 h-4 w-4" /> Satış Analitiği</TabsTrigger>
                    <TabsTrigger value="musteriler" data-testid="hub-tab-customers"><Users className="mr-2 h-4 w-4" /> Müşterilerim {customers && <Badge variant="outline" className="ml-1.5 text-[10px]">{customers.length}</Badge>}</TabsTrigger>
                    <TabsTrigger value="kampanyalar" data-testid="hub-tab-campaigns"><BadgePercent className="mr-2 h-4 w-4" /> Kampanyalar {campaigns && <Badge variant="outline" className="ml-1.5 text-[10px]">{campaigns.length}</Badge>}</TabsTrigger>
                    <TabsTrigger value="teklifler" data-testid="hub-tab-quotes"><FileText className="mr-2 h-4 w-4" /> Teklif Performansı</TabsTrigger>
                    <TabsTrigger value="sorular" data-testid="hub-tab-questions"><HelpCircle className="mr-2 h-4 w-4" /> Sorular {questions && <Badge variant="outline" className={`ml-1.5 text-[10px] ${questions.some((q) => !q.answer) ? "border-amber-300 bg-amber-100 text-amber-800" : ""}`}>{questions.filter((q) => !q.answer).length} bekleyen</Badge>}</TabsTrigger>
                </TabsList>

                <TabsContent value="analitik" className="mt-4 space-y-6" data-testid="hub-analitik">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {kpiCards.map((k) => (
                            <div key={k.tid} className="rounded-2xl border bg-card p-5 shadow-sm" data-testid={k.tid}>
                                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k.label}</p>
                                <p className="mt-2 font-display text-2xl font-extrabold">{k.value}</p>
                            </div>
                        ))}
                    </div>
                    {tahsilat && (
                        <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="tahsilat-ozeti">
                            <div className="flex items-center gap-2">
                                <Wallet className="h-4 w-4 text-terra-600" />
                                <h2 className="font-display font-bold">Tahsilat Özeti</h2>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
                                <div data-testid="tahsilat-open">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Açık Alacak</p>
                                    <p className="mt-1 font-display text-xl font-extrabold">{fmtTL(tahsilat.openTotal)}</p>
                                    <p className="text-xs text-muted-foreground">{openInv.length} fatura</p>
                                </div>
                                <div data-testid="tahsilat-overdue">
                                    <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-600"><AlertTriangle className="h-3 w-3" /> Vadesi Geçen</p>
                                    <p className={`mt-1 font-display text-xl font-extrabold ${tahsilat.overdueCount ? "text-red-700" : ""}`}>{fmtTL(tahsilat.overdueTotal)}</p>
                                    <p className="text-xs text-muted-foreground">{tahsilat.overdueCount} fatura</p>
                                </div>
                                <div data-testid="tahsilat-upcoming">
                                    <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700"><CalendarClock className="h-3 w-3" /> 7 Gün İçinde</p>
                                    <p className="mt-1 font-display text-xl font-extrabold">{fmtTL(tahsilat.upcomingTotal)}</p>
                                    <p className="text-xs text-muted-foreground">{tahsilat.upcomingCount} fatura</p>
                                </div>
                                <div data-testid="tahsilat-confirm">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Onayınızı Bekleyen</p>
                                    <p className="mt-1 font-display text-xl font-extrabold">{tahsilat.confirmCount}</p>
                                    <p className="text-xs text-muted-foreground">ödeme bildirimi</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                        <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-7" data-testid="hub-monthly-chart">
                            <h2 className="font-display text-lg font-bold">Aylık Satış Trendi</h2>
                            {analytics?.monthly?.length ? (
                                <div className="mt-4">
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={analytics.monthly}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E5DCD3" />
                                            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#68615A" />
                                            <YAxis tick={{ fontSize: 11 }} stroke="#68615A" tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
                                            <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5DCD3" }} />
                                            <Bar dataKey="total" name="Ciro" fill="#2D4A3E" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : <p className="py-16 text-center text-sm text-muted-foreground">Henüz satış verisi yok.</p>}
                        </div>
                        <div className="overflow-hidden rounded-2xl border bg-card shadow-sm lg:col-span-5" data-testid="hub-top-products">
                            <div className="border-b bg-muted/50 px-5 py-3"><h2 className="font-display font-bold">En Çok Satan Ürünler</h2></div>
                            <table className="w-full text-sm">
                                <tbody>
                                    {(analytics?.top_products || []).map((p, i) => (
                                        <tr key={p.name} className="border-b last:border-0" data-testid={`hub-top-product-${i}`}>
                                            <td className="p-3.5 pl-5">
                                                <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold">{i + 1}</span>
                                                {p.name}
                                            </td>
                                            <td className="p-3.5 text-muted-foreground">{p.qty} birim</td>
                                            <td className="p-3.5 pr-5 text-right font-display font-extrabold text-sage-600">{fmtTL(p.revenue)}</td>
                                        </tr>
                                    ))}
                                    {!analytics?.top_products?.length && <tr><td className="p-10 text-center text-sm text-muted-foreground">Veri yok.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="musteriler" className="mt-4" data-testid="hub-customers">
                    {!customers ? (
                        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-muted" />)}</div>
                    ) : (
                        <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                            <table className="w-full text-sm" data-testid="hub-customers-table">
                                <thead>
                                    <tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                        <th className="p-4">Müşteri (Mağaza/İşletme)</th>
                                        <th className="p-4">İletişim</th>
                                        <th className="p-4">Sipariş</th>
                                        <th className="p-4">Toplam Alım</th>
                                        <th className="p-4">Son Sipariş</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.map((c, i) => (
                                        <tr key={c.company} className="border-b last:border-0 hover:bg-muted/40" data-testid={`hub-customer-${i}`}>
                                            <td className="p-4">
                                                <span className="font-semibold">{c.company}</span>
                                                {i === 0 && <Badge className="ml-2 border-0 bg-saffron-400 text-[10px] text-stone-900"><Crown className="mr-1 h-3 w-3" />En iyi müşteri</Badge>}
                                            </td>
                                            <td className="p-4 text-muted-foreground">{c.email || "—"}</td>
                                            <td className="p-4">{c.orders}</td>
                                            <td className="p-4 font-display font-extrabold text-terra-600">{fmtTL(c.total)}</td>
                                            <td className="p-4 text-muted-foreground">{fmtDate(c.last_order_at)}</td>
                                        </tr>
                                    ))}
                                    {!customers.length && <tr><td colSpan={5} className="p-16 text-center text-sm text-muted-foreground" data-testid="hub-customers-empty">Henüz müşteri yok — ilk siparişinizi bekleyin.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="kampanyalar" className="mt-4" data-testid="hub-campaigns">
                    {!campaigns ? (
                        <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted" />)}</div>
                    ) : (
                        <div className="space-y-3">
                            {campaigns.map((c) => {
                                const product = offers.find((o) => o.offer_id === c.offer_id);
                                return (
                                    <div key={c.campaign_id} className={`flex flex-wrap items-center gap-4 rounded-2xl border bg-card p-5 shadow-sm ${!c.active ? "opacity-55" : ""}`} data-testid={`campaign-row-${c.campaign_id}`}>
                                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100">
                                            <BadgePercent className="h-5 w-5 text-amber-700" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-display font-bold">{c.name}</p>
                                                <Badge variant="outline" className={`text-[10px] ${c.is_live ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-stone-200 bg-stone-100 text-stone-600"}`} data-testid={`campaign-status-${c.campaign_id}`}>
                                                    {c.is_live ? "Yayında" : c.active ? "Süresi Doldu" : "Duraklatıldı"}
                                                </Badge>
                                            </div>
                                            <p className="mt-0.5 text-xs text-muted-foreground">
                                                {product?.product_name || c.product_id} • %{c.discount_percent} indirim
                                                {product && <> — <span className="line-through">{fmtTL(product.price)}</span> → <strong className="text-terra-600">{fmtTL(product.price * (100 - c.discount_percent) / 100)}</strong></>}
                                                {" "}• Bitiş: {fmtDate(c.ends_at)}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => toggleCamp(c)} data-testid={`campaign-toggle-${c.campaign_id}`}>
                                                <Power className="mr-1.5 h-3.5 w-3.5" /> {c.active ? "Duraklat" : "Aktifleştir"}
                                            </Button>
                                            <Button size="sm" variant="outline" className="text-destructive" onClick={() => deleteCamp(c)} data-testid={`campaign-delete-${c.campaign_id}`}>
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                            {!campaigns.length && (
                                <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="campaigns-empty">
                                    <BadgePercent className="mx-auto h-10 w-10 text-muted-foreground" />
                                    <p className="mt-3 text-sm text-muted-foreground">Henüz kampanya yok. İlk kampanyanızı oluşturun — indirimli fiyat alıcılara anında yansır.</p>
                                </div>
                            )}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="teklifler" className="mt-4 space-y-6" data-testid="hub-quotes">
                    {quoteStats && (
                        <>
                            <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                                {[
                                    { label: "Verilen Teklif", value: quoteStats.total, tid: "hub-quote-total" },
                                    { label: "Bekleyen", value: quoteStats.pending, tid: "hub-quote-pending" },
                                    { label: "Kazanılan", value: quoteStats.accepted, tid: "hub-quote-accepted" },
                                    { label: "Kazanma Oranı", value: `%${quoteStats.win_rate}`, tid: "hub-quote-winrate" },
                                    { label: "Teklif Bekleyen RFQ", value: quoteStats.waiting_rfqs, tid: "hub-quote-waiting" },
                                ].map((k) => (
                                    <div key={k.tid} className="rounded-2xl border bg-card p-4 shadow-sm" data-testid={k.tid}>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{k.label}</p>
                                        <p className="mt-1.5 font-display text-xl font-extrabold">{k.value}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="overflow-hidden rounded-2xl border bg-card shadow-sm" data-testid="hub-quotes-table">
                                <div className="border-b bg-muted/50 px-5 py-3"><h2 className="font-display font-bold">Son Tekliflerim</h2></div>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                            <th className="p-4">RFQ</th>
                                            <th className="p-4">Alıcı</th>
                                            <th className="p-4">Teklifim</th>
                                            <th className="p-4">Durum</th>
                                            <th className="p-4">Tarih</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quoteStats.recent.map((q) => (
                                            <tr key={q.quote_id} className="border-b last:border-0" data-testid={`hub-quote-row-${q.quote_id}`}>
                                                <td className="p-4 font-semibold">{q.rfq_no}</td>
                                                <td className="p-4 text-muted-foreground">{q.buyer_company_name}</td>
                                                <td className="p-4 font-display font-extrabold text-terra-600">{fmtTL(q.total)}</td>
                                                <td className="p-4"><Badge variant="outline" className={`text-[10px] ${QUOTE_STATUS[q.status]?.cls}`}>{QUOTE_STATUS[q.status]?.label}</Badge></td>
                                                <td className="p-4 text-muted-foreground">{fmtDate(q.created_at)}</td>
                                            </tr>
                                        ))}
                                        {!quoteStats.recent.length && <tr><td colSpan={5} className="p-16 text-center text-sm text-muted-foreground">Henüz teklif vermediniz — Teklifler (RFQ) sayfasından açık taleplere teklif verin.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </TabsContent>
                <TabsContent value="sorular" className="mt-4" data-testid="hub-questions">
                    {!questions ? (
                        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted" />)}</div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">Mağazalardan gelen sorular — yanıt süresi 1 takvim günüdür (Pazar tatil günüdür).</p>
                            {questions.map((q) => (
                                <div key={q.question_id} className={`rounded-2xl border bg-card p-5 shadow-sm ${!q.answer && q.overdue ? "border-red-300" : ""}`} data-testid={`hub-question-${q.question_id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                            <p className="font-semibold">{q.buyer_company_name} {q.product_name && <span className="font-normal text-muted-foreground">— {q.product_name}</span>}</p>
                                            <p className="text-xs text-muted-foreground">{fmtDate(q.created_at)} • Yanıt son: {fmtDate(q.answer_due_at)}</p>
                                        </div>
                                        {q.answer ? (
                                            <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-[10px] text-emerald-800">Yanıtlandı{q.answered_late ? " (geç)" : ""}</Badge>
                                        ) : q.overdue ? (
                                            <Badge variant="outline" className="border-red-300 bg-red-100 text-[10px] font-semibold text-red-700" data-testid={`question-overdue-${q.question_id}`}>Süre Doldu</Badge>
                                        ) : (
                                            <Badge variant="outline" className="border-amber-200 bg-amber-100 text-[10px] text-amber-800">Yanıt Bekliyor</Badge>
                                        )}
                                    </div>
                                    <p className="mt-2 text-sm">{q.content}</p>
                                    {q.answer ? (
                                        <p className="mt-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900"><strong>Yanıtınız:</strong> {q.answer}</p>
                                    ) : (
                                        <div className="mt-3 flex gap-2">
                                            <Input value={answers[q.question_id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.question_id]: e.target.value }))} placeholder="Yanıtınızı yazın..." data-testid={`question-answer-input-${q.question_id}`} />
                                            <Button onClick={() => answerQuestion(q)} disabled={ansBusy === q.question_id} className="shrink-0 bg-terra-600 hover:bg-terra-700 text-white" data-testid={`question-answer-submit-${q.question_id}`}>
                                                {ansBusy === q.question_id ? "..." : "Yanıtla"}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {!questions.length && (
                                <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="questions-empty">
                                    <HelpCircle className="mx-auto h-10 w-10 text-muted-foreground" />
                                    <p className="mt-3 text-sm text-muted-foreground">Henüz soru yok — mağazalar ürün sayfalarından soru sorabilir.</p>
                                </div>
                            )}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <Dialog open={campOpen} onOpenChange={setCampOpen}>
                <DialogContent data-testid="campaign-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Yeni Kampanya / İndirim</DialogTitle>
                        <DialogDescription>Seçtiğiniz üründe indirimli fiyat, süre boyunca tüm alıcılara gösterilir.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <p className="mb-1 text-xs font-medium text-muted-foreground">Ürün *</p>
                            <Select value={campForm.offer_id} onValueChange={(v) => setCampForm({ ...campForm, offer_id: v })}>
                                <SelectTrigger data-testid="campaign-offer-select"><SelectValue placeholder="Ürün seçin..." /></SelectTrigger>
                                <SelectContent>
                                    {offers.map((o) => <SelectItem key={o.offer_id} value={o.offer_id}>{o.product_name} — {fmtTL(o.price)}/{o.unit}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <Input placeholder="Kampanya adı * (örn: Ekim Fırsatı)" value={campForm.name} onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} data-testid="campaign-name-input" />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="mb-1 text-xs font-medium text-muted-foreground">İndirim (%)</p>
                                <Input type="number" min="1" max="90" value={campForm.discount_percent} onChange={(e) => setCampForm({ ...campForm, discount_percent: e.target.value })} data-testid="campaign-discount-input" />
                            </div>
                            <div>
                                <p className="mb-1 text-xs font-medium text-muted-foreground">Süre (gün)</p>
                                <Input type="number" min="1" value={campForm.days} onChange={(e) => setCampForm({ ...campForm, days: e.target.value })} data-testid="campaign-days-input" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCampOpen(false)}>Vazgeç</Button>
                        <Button onClick={createCampaign} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="campaign-submit">
                            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Kampanyayı Yayınla
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
